import {
  IMAGE_PREFIX_ID, 
  MASK_PREFIX_ID,
} from '@frontend/constants/templates-field-prefixes';
import API_MEDIA from '../../frontend/services/api/media';
import {
  calculateDataForAllProductImages, getLockValue, getShadowFabricInstance,
  isInGroup,
  removeObjectFromCanvas,
  updateBoundingBoxInfo
} from '@frontend/utils';
import { ShowPageScopeCollector } from '@frontend/group/modules/scope-collectors/show-page-collector';
import { FIELD_TYPE_PRODUCT_IMAGE } from '@frontend/constants/type-fields-of-template';
import { formDataToObject } from '@frontend/lib/utils/form-data-to-object';
import { scaleControlsCornerSize } from '@frontend/constants/fabric-constants';
import {getGroupNameByAddFieldId} from "@/common/preview/draw-smart-object";
import store from '@frontend/store';
import { fabric } from "fabric";
const getScopePage = () => ShowPageScopeCollector.get([
  'canvas_data',
  'updateLayoutChanges',
  'selectedObjects',
  'reorderObjectCanvas',
  'layout_options'
]);
const countrySelect = $('select[name="country_id"]');
const languageSelect = $('select[name="language_id"]');
const bannerViewCache = new Map();

function restoreDisplayBuilderProductLayout(canvas) {
  if (!window.isDisplayBuilder) {
    return;
  }

  if (
    canvas?.displayBuilderAutoFillRequest
    || window.displayBuilderPanel?.isAutoFillingShelves
  ) {
    return;
  }

  const helper = window.displayBuilderHelper;

  if (!helper || helper.canvas !== canvas) {
    return;
  }

  helper.restoreProductAssignmentsFromState?.();
  helper.reflowAllShelfProducts?.({ forceWarp: true });
}

async function drawProductPlaceholder(ProductImagesFieldsOptions, canvas, templateInstance, file_ids) {
  try {
    if (!Array.isArray(ProductImagesFieldsOptions)) {
      return
    }
    const {reorderObjectCanvas } = getScopePage();
    for (const [index, productOption] of ProductImagesFieldsOptions.entries()) {
      if(file_ids && file_ids[index]) {
        continue
      }
      const {
        Filename, Width = 300, Height = 300, X = 0, Y = 0, Order = 2000,
      } = productOption;
      if (!Filename) continue
      const imageUrl = `${window.location.origin}/share?file=${encodeURIComponent(Filename)}`;

      const img = await new Promise((resolve, reject) => {
        fabric.Image.fromURL(imageUrl, loadedImg => resolve(loadedImg), {crossOrigin: 'anonymous'});
      });

      const scaleX = Width / img.width;
      const scaleY = Height / img.height;
      const scale = Math.min(scaleX, scaleY);
      const fieldsProductImage = templateInstance.getFieldsByType(FIELD_TYPE_PRODUCT_IMAGE);
      img.set({
        id: 'image_' + index,
        element_id: fieldsProductImage[index]?.element_id || IMAGE_PREFIX_ID + index,
        fieldId: fieldsProductImage[index]?.id,
        order: parseInt(Order),
        originX: 'left',
        originY: 'top',
        left: parseInt(X),
        top: parseInt(Y),
        selectable: true,
      });

      img.scale(scale);
      canvas.add(img);
      canvas.renderAll();
      reorderObjectCanvas(canvas);
    }
  } catch (e) {
    toastr.error('Error when rendering product placeholder');
    console.error(e);
  }
}

const needToDrawAsPlaceholder = (boundingData, imageFilename) => {
  const isUrl = typeof imageFilename === 'string' &&
    (imageFilename.startsWith('http://') || imageFilename.startsWith('https://'));
  const hasBoundingData = boundingData &&
    (typeof boundingData === 'object' ? Object.keys(boundingData).length > 0 : true);

  return isUrl && !hasBoundingData;
};

export async function drawProductImage({
  data,
  canvas,
  id,
  shadows,
  group_fields,
  dimension,
  previewBoundingBoxData,
  ignoreImageCache = false,
  isRedrawing = false,
  updateAllTemplates = true,
  updateLayerOrder = true,
}) {
  const prevObjWithDataForRestoreOriginalPaths = canvas.getObjects().find(obj => obj.id.startsWith(IMAGE_PREFIX_ID)
    && Object.hasOwn(obj, 'originalPathForRestoreAfterCrop')
    && obj.originalPathForRestoreAfterCrop);
  const originalPathForRestoreAfterCrop = 
    prevObjWithDataForRestoreOriginalPaths ? prevObjWithDataForRestoreOriginalPaths.originalPathForRestoreAfterCrop : '';
  removeObjectFromCanvas(canvas, IMAGE_PREFIX_ID);
  const { canvas_data, updateLayoutChanges, reorderObjectCanvas } = getScopePage();
  canvas_data[id].originCoords = [];
  
  let file_ids = (data['file_ids'] || '').split(/[, ]+/);
  if (data.product_texts) {
    const country = countrySelect.val();
    const language = languageSelect.val();
    const product_texts = JSON.parse(data.product_texts);
    const text_data = product_texts[country + '_' + language];
    
    if (text_data && text_data.productIDs) {
      file_ids = text_data.productIDs.split(/[, ]+/);
    }
  }

  let layout_id = canvas_data[id]._data['layout_id'];
  file_ids = canvas_data[id].product_image_settings
    .slice(0, file_ids.length)
    .map((options, i) => {
      // Always preserve Salsify products regardless of group settings

      // TODO: recheck this
      if (file_ids[i] && (file_ids[i].toString().startsWith('salsify:') || file_ids[i].toString().startsWith('asin:'))) {
        return file_ids[i];
      }
      if (!group_fields || isInGroup(group_fields, options['Name'])) {
        return file_ids[i];
      }
      return null;
    })
    .filter((file_id) => file_id !== null);

  const { getBannerView } = API_MEDIA;

  // Build file_paths aligned with requested file_ids (index-based on backend)
  // so Salsify/ASIN IDs don't accidentally pick up another item's path.
  let file_paths = null;

  try {
    if (store && store.hasModule && store.hasModule('fileBrowserProductSelector')) {
      const pickedItems = store.getters['fileBrowserProductSelector/pickedItems'] || [];
      const pathByFileId = new Map(
        pickedItems.map((item) => ([
          item?.fileId != null ? String(item.fileId) : '',
          item?.original_path || item?.path || item?.fileId
        ]))
      );

      file_paths = file_ids
        .map((fileId) => {
          const key = fileId != null ? String(fileId) : '';
          return pathByFileId.get(key) || fileId;
        })
        .join(' ');
    }
  } catch (error) {
    console.warn('Could not access store for file_paths:', error);
  }

  const requestParams = {
    file_ids: file_ids.join(' '),
    show_warning: true,
    layout_id: layout_id
  };

  // Add file_paths if available
  if (file_paths) {
    requestParams.file_paths = file_paths;
  }

  //there's no need to api call if nothing to request
  if (
    (!file_ids.length || !file_ids.some(v => typeof v === 'string' && v.trim() !== '')) &&
    !file_paths
  ) {
    await drawProductPlaceholder(canvas_data[id].product_image_settings, canvas,canvas_data[id].templateInstance)
    if (window.isDisplayBuilder) {
      restoreDisplayBuilderProductLayout(canvas);
    }
    return;
  }
  if (file_ids.length !== canvas_data[id].product_image_settings.length) {
    await drawProductPlaceholder(canvas_data[id].product_image_settings, canvas,canvas_data[id].templateInstance, file_ids)
  }
  const cacheKeyParts = [
    file_ids.join(','),
    layout_id,
    file_paths ? JSON.stringify(file_paths) : ''
  ];
  const cacheKey = cacheKeyParts.join('|');
  
  let fileIdsData;
  if (!ignoreImageCache && bannerViewCache.has(cacheKey)) {
    const cached = bannerViewCache.get(cacheKey);
    fileIdsData = await cached;
  } else {
    const requestPromise = getBannerView(requestParams)
      .then(response => response.data)
      .catch(err => {
        bannerViewCache.delete(cacheKey);
        throw err;
      });

    bannerViewCache.set(cacheKey, requestPromise);
    fileIdsData = await requestPromise;
  }

  // const { data: fileIdsData } = await getBannerView(requestParams);
  

  const firstFilePath = fileIdsData.files?.[0]?.path;
  if (firstFilePath) {
    // getDominantColors(firstFilePath, id);
  }


  let files = fileIdsData.files;

  if (!files) return;

  let {
    left,
    top,
    product_height,
    margin,
    imagesData
  } = await calculateDataForAllProductImages(
    files,
    canvas_data[id],
    data
  );

  const fieldsProductImage = canvas_data[id].templateInstance.getFieldsByType(FIELD_TYPE_PRODUCT_IMAGE);

  /* used to save bounding box info */
  const formData = new FormData();
  formData.append('type', 'Product Image');
  canvas.renderOnAddRemove = false;

  let hasPlaceHolderRecalculate = false
  let newScale = [];
  let newXOffset = [];
  let newYOffset = [];
  const productDimension = canvas_data[id].product
  for (const [index, item] of imagesData.entries()) {
    const { config, x_offset, y_offset, scale, accLeft } = createObjectOfProductImage(
      { left, top, margin, product_height, item },
      index,
      canvas_data[id],
      data,
      dimension,
      shadows
    );
    let auto_position = true;

    if (['0', 'false', false, 0].includes(canvas_data[id]?._data?.product_auto_positioning)) {
      auto_position = false;
    }

    if (auto_position)
      left = accLeft;
    
    if (config.groupName && isRedrawing) {
      const smartObject = canvas.getObjects().find(obj => obj.id?.startsWith(MASK_PREFIX_ID) && obj.groupName === config.groupName);
      
      if (!smartObject) {
        console.warn(`Smart object with groupName "${config.groupName}" was not found on the canvas.`);
      } else {
        config.smartObject = smartObject;
      }
    }

    item.image.set({
      ...config,
      templateField: fieldsProductImage?.[index] || null,
      fieldId: fieldsProductImage?.[index].id,
      updateLayoutChanges,
      deleteLayer,
      getShadowData,
      setShadowData,
      clearShadowData,
      deleteData,
    });
    
    if (
      prevObjWithDataForRestoreOriginalPaths 
      && item.image.id === prevObjWithDataForRestoreOriginalPaths.id 
      && originalPathForRestoreAfterCrop
    ) {
      item.image.originalPathForRestoreAfterCrop = originalPathForRestoreAfterCrop;
    }
    
    scaleControlsCornerSize(item.image);

    //display image that not placeholder(http or https) or witch has _bounding_data
    const imageFilename = item.image.fileName;
    const boundingDataKey = `${item.image.element_id}_bounding_data`;
    const boundingData = canvas_data[id]._data[boundingDataKey];
    const canvasDataScale = canvas_data[id]._data['scale'];
    const canvasDataX_offset = canvas_data[id]._data['x_offset'];
    const canvasDataY_offset = canvas_data[id]._data['y_offset'];
    const targetFieldSettings = canvas_data[id].product_image_settings.find(
      (setting) => setting.id === item.image.fieldId
    );
    let updateScale = null;
    let updateX_offset = null;
    let updateY_offset = null;
    if (targetFieldSettings && needToDrawAsPlaceholder(boundingData, imageFilename)) {
      hasPlaceHolderRecalculate = true;
      const {Filename, Width, Height, X, Y} = targetFieldSettings
      if (Filename) {
        const scaleX = Width / item.image.width;
        const scaleY = Height / item.image.height;
        const scale = Math.min(scaleX, scaleY);
        item.image.set({
          originX: 'left',
          originY: 'top',
          left: parseInt(X),
          top: parseInt(Y),
        });
        item.image.scale(scale);
        //also need to update scale in canvas_data
        updateScale = Math.min(Width, Height) / item.width;
        //calculates new offsets - to correct display after 2th render
        updateX_offset = (productDimension.width - Width) / 2 + productDimension.left;
        updateY_offset = (productDimension.height - Height) / 2 + productDimension.top;
      }
    }
    if (!canvas.getObjects().some(object => object.id === item.image.id)) {
      canvas.add(item.image);
    }

    /* update bounding box info */
    let bound = item.image.getBoundingRect();
    let elementId = item.image.element_id;
    formData.append('product_image_index[]', index);
    formData.append('element_ids[]', elementId);
    formData.append('p_width[]', bound.width);
    formData.append('p_height[]', bound.height);
    formData.append(`${elementId}_bounding_data`, JSON.stringify(bound));
    if (canvasDataScale.length) {
      newScale.push(updateScale ? updateScale.toFixed(3) : canvasDataScale[index])
    }
    if (canvasDataX_offset.length) {
      newXOffset.push(updateX_offset ? (canvasDataX_offset[index] - updateX_offset) : canvasDataX_offset[index]);
    }
    if (canvasDataY_offset.length) {
      newYOffset.push(updateY_offset ? (canvasDataY_offset[index] - updateY_offset) : canvasDataY_offset[index]);
    }

    canvas_data[id].originCoords.push({
      x: item.image.left - x_offset,
      y: item.image.top - y_offset,
      scaleX: item.image.scaleX / scale,
    });
    replaceSelectedObjectById(item.image);
  };
  if (hasPlaceHolderRecalculate && newScale.length) {
    for (const scaleValue of newScale) {
      formData.append('scale[]', scaleValue);
    }
    for (const xOffsetValue of newXOffset) {
      formData.append('x_offset[]', xOffsetValue);
    }
    for (const yOffsetValue of newYOffset) {
      formData.append('y_offset[]', yOffsetValue);
    }
  }
  canvas.renderOnAddRemove = true;
  canvas.renderAll();

  /* UPDATE BOUNDING BOX INFO */
  if (previewBoundingBoxData) {
    previewBoundingBoxData.data.push(formDataToObject(formData));
  } else {
    updateBoundingBoxInfo(formData, id);
  }

  reorderObjectCanvas(canvas, { updateAllTemplates, updateLayerOrder });
  if (window.isDisplayBuilder) {
    restoreDisplayBuilderProductLayout(canvas);
  }
}

const replaceSelectedObjectById = function (newObject) {
  const { selectedObjects } = getScopePage();

  if (selectedObjects.length === 0) {
    return;
  }

  const currentObject = selectedObjects[0];
  if (currentObject.type === 'activeSelection') {
    for (const key in currentObject._objects) {
      if (currentObject._objects[key].id === newObject.id) {
        selectedObjects[0]._objects[key] = newObject;
        return;
      }
    }
  } else if (currentObject.id === newObject.id) {
    selectedObjects[0] = newObject;
  }
};

export function createObjectOfProductImage(
  { left, top, margin, product_height, item },
  index,
  canvasData,
  data,
  dimension,
  shadows,
  fieldsProductImage = null
) {
  const angle = +data?.angle?.[index] || 0;
  const x_offset = +data?.x_offset?.[index] || 0;
  const y_offset = +data?.y_offset?.[index] || 0;
  let scale = +data?.scale?.[index] || 1;
  const moveable = data.moveable && data.moveable[index] !== undefined ? data.moveable[index] : false;
  let bShadow = +data?.p_shadow?.[index];
  const opacity = (Number.isFinite(data?.p_opacity?.[index])) ? +data?.p_opacity?.[index] : 1;

  if (!fieldsProductImage) {
    fieldsProductImage = canvasData.templateInstance.getFieldsByType(FIELD_TYPE_PRODUCT_IMAGE);
  }
  fieldsProductImage = fieldsProductImage[index];
  const productImageSettings = canvasData.product_image_settings[index];
  const visible = data?.p_visible?.[index] ?? (!productImageSettings['Hidden'] || true);
  const ovalShadow = data?.p_oval_shadows?.[index]?.length ? data?.p_oval_shadows?.[index] : null;
  const mirrorShadow = data?.p_mirror_shadows?.[index]?.length ? data?.p_mirror_shadows?.[index] : null;

  const ovalShadowGroupId = data?.p_oval_shadow_group_ids?.length ? data?.p_oval_shadow_group_ids?.[index] : null;
  const mirrorShadowGroupId = data?.p_mirror_shadow_group_ids?.length ? data?.p_mirror_shadow_group_ids?.[index] : null;
  let config = {
    left: 0,
    top: 0,
  };

  if (productImageSettings['Option1'] === 'Hero') {
    const { Width: productImageWidth, Height: productImageHeight, Option2: option2, X, Y } = productImageSettings;
    // Ensure minimum size for visibility (25% of smaller canvas dimension)
    const canvasWidth = canvasData.dimension?.width || 800;
    const canvasHeight = canvasData.dimension?.height || 600;
    const minSize = Math.min(canvasWidth, canvasHeight) * 0.25;

    let w = parseInt(productImageWidth) || minSize;
    let h = parseInt(productImageHeight) || minSize;

    // If width/height are too small, use reasonable defaults
    if (w < minSize) w = minSize;
    if (h < minSize) h = minSize;

    const ratio1 = w / item.width;
    let calculatedH = item.height * ratio1;
    const ratio2 = calculatedH / h;

    if (ratio2 > 1) {
      calculatedH = h;
      w = w / ratio2;
    }
    let hero_left = parseInt(X) + w / 2;
    const hero_top = parseInt(Y) + calculatedH / 2;

    if (typeof option2 === 'string' && option2.startsWith('W-')) {
      hero_left = canvasData.dimension['width'] - +option2.split('-')[1] - w / 2;
    }

    config.left = hero_left + x_offset;
    config.top = hero_top + y_offset;
    item.image.scaleToWidth(w);
  } else if (data.is_product_needs_centering && data.is_product_needs_centering === 'true') {
    let productDimensions = canvasData.product;
    // Ensure minimum size for visibility (25% of smaller canvas dimension)
    const canvasCenterWidth = canvasData.dimension?.width || 800;
    const canvasCenterHeight = canvasData.dimension?.height || 600;
    const minCenterSize = Math.min(canvasCenterWidth, canvasCenterHeight) * 0.25;

    let boxWidth, boxHeight;
    if (fieldsProductImage?.options?.scale === 1) {
      boxWidth = productDimensions.width || parseInt(productImageSettings['Width']) || minCenterSize;
      boxHeight = productDimensions.height || parseInt(productImageSettings['Height']) || minCenterSize;
    } else {
      boxWidth = fieldsProductImage.options.width || item.width * scale || minCenterSize;
      boxHeight = fieldsProductImage.options.height || item.height * scale || minCenterSize;
    }
    // Ensure box dimensions are at least the minimum size
    if (boxWidth < minCenterSize) boxWidth = minCenterSize;
    if (boxHeight < minCenterSize) boxHeight = minCenterSize;
    

    const originalWidth = item.image.width;
    const originalHeight = item.image.height;

    const scaleRatio = Math.min(
      boxWidth / originalWidth,
      boxHeight / originalHeight,
      1
    );

    const scaledWidth = originalWidth * scaleRatio;
    const scaledHeight = originalHeight * scaleRatio;

    item.image.scale(scaleRatio);
    scale = 1; //set scale to 1, so after scaling image to 0.2, we will get 0.2*1 = 0.2 scaleX OR scaleY in config below
    
    let offsetX, offsetY;
    if (fieldsProductImage?.options?.scale === 1) {
      const productLeft = +canvasData.product?.left || 0;
      const productTop = +canvasData.product?.top || 0;

      config.left = productLeft + boxWidth / 2 + x_offset;
      config.top = productTop + boxHeight / 2 + y_offset;
    } else {
      offsetX = (boxWidth - scaledWidth) / 2;
      offsetY = (boxHeight - scaledHeight) / 2;

      config.left = left
        + (canvasData.product?.left || 0)
        + offsetX
        + scaledWidth / 2
        + x_offset;

      config.top = top
        + (product_height - boxHeight) / 2
        + offsetY
        + scaledHeight / 2
        + y_offset;
    }

    left += boxWidth + margin;
  } else {
    let wr = item.width * scale;
    let hr = item.height * scale;
    // Handle case where canvasData.product might be empty/undefined
    const productLeft = canvasData.product?.['left'] || 0;

    config.left = left + productLeft + item.width / 2 + x_offset;
    config.top = top + (product_height - hr) / 2 + y_offset + hr / 2;
    item.image.scaleToWidth(item.width);

    left += wr / 2 + item.width / 2 + margin;
  }

  config = {
    ...config,
    id: IMAGE_PREFIX_ID + index,
    element_id: fieldsProductImage?.element_id || IMAGE_PREFIX_ID + index,
    originX: 'center',
    originY: 'center',
    lockUniScaling: true,
    selectable: moveable === 'Yes',
    evented: moveable === 'Yes',
    defaultWidth: +productImageSettings['Width'],
    angle,
    order: +productImageSettings['Order'] || fieldsProductImage?.order || 0,
    scaleX: item.image.scaleX * scale,
    scaleY: item.image.scaleY * scale,
    groupName: getGroupNameByAddFieldId(
      String(productImageSettings.id),
      canvasData
    ),
    flipX: !!+data?.p_flipX?.[index],
    flipY: !!+data?.p_flipY?.[index],
    fieldId: productImageSettings.id,
    fieldType: FIELD_TYPE_PRODUCT_IMAGE,
    opacity,
    ovalShadow,
    ovalShadowGroupId,
    mirrorShadow,
    mirrorShadowGroupId,
    gridLayoutInstanceId: canvasData._data.instance_id,
    visible
  };

  config.shadow = getShadowFabricInstance(data.instance_id, config.element_id, shadows, bShadow, config.scaleX, config.scaleY);
  config.locked = getLockValue(data.instance_id, config.element_id);

  let group_x = 0;
  let group_y = 0;
  const { groupName } = config;

  if (groupName && canvasData.smartObjCoords[groupName]) {
    group_x = +canvasData.smartObjCoords[groupName]['X'];
    group_y = +canvasData.smartObjCoords[groupName]['Y'];
  }

  config = {
    ...config,
    left: config.left + group_x - dimension[2] + dimension[4].x,
    top: config.top + group_y - dimension[3] + dimension[4].y,
  };

  if (isNaN(config.scaleX) || isNaN(config.scaleY)) {
    config = {
      ...config,
      scaleX: '0.37',
      scaleY: '0.37',
      left: productImageSettings.X,
      top: productImageSettings.Y
    };
  }

  return { config, x_offset, y_offset, scale, accLeft: left };
}
async function deleteLayer({ updateAllTemplates = true } = {}) {
  const {canvas, templateField} = this;

  if (!canvas || !templateField) {
    return;
  }

  const preview = window.previews.find(preview => preview.id === canvas.instance_id);

  const deletedLayersArray = [...(preview?.data?.deleted_layers ?? [])];
  deletedLayersArray.push(templateField.id);
  const keys = [
    'file_ids', 'angle', 'x_offset', 'y_offset', 'scale', 'p_shadow',
    'p_opacity', 'p_visible', 'p_oval_shadows', 'p_mirror_shadows', 'p_flipX', 'p_flipY',
    'p_width', 'p_height', 'moveable'
  ];
  const index = parseInt(this.id.split('_').pop());
  const updated = {};
  for (const key of keys) {
    if (!preview.data?.[key]) continue
    if (key === 'file_ids') {
      updated[key] = preview.data[key]
        .split(/[, ]+/)
        .filter((_, i) => i !== index)
        .join(',');
      continue
    }
    updated[key] = [...preview.data[key]].filter((_, i) => i !== index);
  }
  
  for (const key of keys) {
    this.updateLayoutChanges(canvas.instance_id, key, updated[key]);
  }
  this.updateLayoutChanges(canvas.instance_id, 'deleted_layers', deletedLayersArray);
  window.canvas_data[preview.id].product_image_settings = window.canvas_data[preview.id].product_image_settings.filter(pi => pi.id !== templateField.id);
  window.canvas_data[preview.id].templateInstance.fields = window.canvas_data[preview.id].templateInstance.fields.filter(f => f.id !== templateField.id)
  preview.template.fields = preview.template.fields.filter((field) => !preview.data.deleted_layers.includes(field.id));
  window.template_data.fields = window.template_data.fields.filter((field) => !preview.data.deleted_layers.includes(field.id));
  
  await drawProductImage({...preview, updateAllTemplates})
}
function shiftDataAfterProductImageDeletion(currentObject, canvas, index, data, updateLayoutChanges) {
  const fileIds = data.file_ids.split(/[, ]+/);
  if (index == fileIds.length) return;
  
  for (let i = index; index <= fileIds.length ; i++ ) {
    if (!fileIds[i]) break;
    const currentObject = canvas.getObjects('image').find(obj => obj.id === `${IMAGE_PREFIX_ID + i}`);
    const nextObject = canvas.getObjects('image').find(obj => obj.id === `${IMAGE_PREFIX_ID + (i + 1)}`);
    
    if (!nextObject) {
      updateLayoutChanges(canvas.instance_id, `${currentObject.element_id}_custom_shadow`, null);
      break
    }
    const nextCustomShadowData = data[`${nextObject.element_id}_custom_shadow`];

    updateLayoutChanges(canvas.instance_id, `${currentObject.element_id}_custom_shadow`, nextCustomShadowData);
  } 
}
function deleteData() {
  const {canvas, templateField} = this;
  const preview = window.previews.find(preview => preview.id === canvas.instance_id);
  const index = parseInt(this.id.split('_').pop());
  const keys = [
    'angle',
    'x_offset',
    'y_offset',
    'scale',
    'p_shadow',
    'p_opacity',
    'p_visible',
    'p_oval_shadows',
    'p_mirror_shadows',
    'p_flipX',
    'p_flipY',
    'p_width',
    'p_height',
    'moveable',
    `${this.element_id}_custom_shadow`,
    `${this.element_id}_bounding_data`,
    `${this.element_id}_alignment`,
  ];
  const updated = {};
  
  for (const key of keys) {
    if (!preview.data?.[key]) continue
    if (key.startsWith(this.element_id)) {
      updated[key] = null;
      continue
    }
    updated[key] = [...preview.data[key]].filter((_, i) => i !== index);
  }

  for (const key of keys) {
    if (key.startsWith(this.element_id)) {
      this.updateLayoutChanges(canvas.instance_id, key, updated[key]);
      continue
    }

    this.updateLayoutChanges(canvas.instance_id, key, updated[key]);
  }
  shiftDataAfterProductImageDeletion(this, this.canvas, index, preview.data, this.updateLayoutChanges)
}
function getShadowData() {
  const shadowCases = ['oval', 'mirror', 'customOutline', 'custom'];
  let result = null;
  const {canvas} = this;
  const preview = window.previews.find(preview => preview.id === canvas.instance_id);
  const prefixId = this.element_id;
  for (const type of shadowCases) {
    switch (type) {
      case 'oval': {
        const key = this.id.split('_').pop();
        result = preview.data?.p_oval_shadows?.[key];
        break;
      }
      case 'mirror': {
        const key = this.id.split('_').pop();
        result = preview.data?.p_mirror_shadows?.[key];
        break;
      }
      case 'customOutline': {
        result = preview.data?.[`${prefixId}_custom_outline_shadow`];
        break;
      }
      case 'custom': {
        result = preview.data?.[`${prefixId}_custom_shadow`];
        break;
      }
    }
    if (result) return {type, value: result};
  }
  return null;
}
function setShadowData({type, value}) {
  const { canvas, id } = this;
  const preview = window.previews.find(p => p.id === canvas.instance_id);
  if (!preview?.data) return false;

  const key = id.split('_').pop();
  const prefixId = this.element_id;
  this.clearShadowData();
  switch (type) {
    case 'oval': {
      const ovalShadowKey = `p_oval_shadows`
      if (!preview.data?.[ovalShadowKey]) {
        preview.data[ovalShadowKey] = [];
      }
      const copyOvalShadowsData = structuredClone(preview.data[ovalShadowKey]);
      copyOvalShadowsData[key] = value;
      this.ovalShadow = value;
      this.canvas.requestRenderAll();
      this.updateLayoutChanges(preview.id, ovalShadowKey, copyOvalShadowsData);
      break;
    }
    case 'mirror': {
      const mirrorShadowKey = `p_mirror_shadows`
      if (!preview.data?.[mirrorShadowKey]) {
        preview.data[mirrorShadowKey] = [];
      }
      const copyMirrorShadowsData = structuredClone(preview.data[mirrorShadowKey]);
      copyMirrorShadowsData[key] = value;
      this.mirrorShadow = value;
      this.canvas.requestRenderAll();
      this.updateLayoutChanges(preview.id, mirrorShadowKey, copyMirrorShadowsData);
      break;
    }
    case 'customOutline': {
      this.updateLayoutChanges(preview.id, `${prefixId}_custom_outline_shadow`, value);
      break;
    }
    case 'custom': {
      this.set('shadow', JSON.parse(value))
      this.canvas.requestRenderAll();
      this.updateLayoutChanges(preview.id, `${prefixId}_custom_shadow`, JSON.stringify(this.shadow))
      break;
    }
    default:
      return false;
  }

  return true;
}

function clearShadowData() {
  const { canvas, id } = this;
  const preview = window.previews.find(p => p.id === canvas.instance_id);
  if (!preview?.data) return false;

  const key = id.split('_').pop();
  const prefixId = this.element_id;

  const ovalShadowKey = `p_oval_shadows`;
  if (preview.data?.[ovalShadowKey]) {
    const copyOvalShadowsData = structuredClone(preview.data[ovalShadowKey]);
    delete copyOvalShadowsData[key];
    this.ovalShadow = null;
    this.updateLayoutChanges(preview.id, ovalShadowKey, copyOvalShadowsData);
  }

  const outlineKey = `${prefixId}_custom_outline_shadow`;
  if (preview.data?.[outlineKey]) {
    delete preview.data[outlineKey];
    this.updateLayoutChanges(preview.id, outlineKey, null);
  }

  const customKey = `${prefixId}_custom_shadow`;
  if (preview.data?.[customKey]) {
    delete preview.data[customKey];
    this.set('shadow', null);
    this.updateLayoutChanges(preview.id, customKey, null);
  }

  this.canvas.requestRenderAll();
  return true;
}
